import { CSS, Flex, Tooltip, Typography } from '@project44/manifest-ds';
import { differenceInMinutes } from '@project44/ui-utils';

import { scopedT } from '../../../utils/functions/scopedT';
import DateDiff from '../DateDiff';
import DateTime, { DateTimeDisplayFormat } from '../DateTime';
import PredictedIcon from './Icons/PredictedIcon';

import {
  DateComponentWrapper,
  PREDICTED_BG_COLOR,
  STALE_BG_COLOR,
} from '../styles';

const t = scopedT();

interface PredictedTimestampProps {
  css?: CSS;
  format?: DateTimeDisplayFormat;
  plannedEndDate?: string;
  plannedStartDate?: string;
  predictedDate?: string;
  timeZone?: string;
}

export const PredictedTimestamp = ({
  css,
  format,
  predictedDate,
  plannedEndDate,
  plannedStartDate,
  timeZone,
}: PredictedTimestampProps) => {
  const diffMinutes = predictedDate
    ? differenceInMinutes(Date.now(), String(predictedDate))
    : 0;

  const isStale = diffMinutes / 60 > 24;
  const bgColor = isStale ? STALE_BG_COLOR : PREDICTED_BG_COLOR;

  const getTooltip = () => {
    switch (format) {
      case DateTimeDisplayFormat.LONG:
      case DateTimeDisplayFormat.STACKED:
        return (
          <Flex orientation="vertical" align="center">
            <Flex>{t('tooltips.p44prediction')} </Flex>
            <Flex>{isStale ? t('tooltips.stale') : ''}</Flex>
          </Flex>
        );
      case DateTimeDisplayFormat.SHORT:
        return (
          <Flex orientation="vertical" align="center">
            <Flex
              align="center"
              css={{ '.middot': { color: '$text-contrast' } }}
            >
              {t('tooltips.p44prediction')}
              :&nbsp;
              <DateTime
                endDateTime={predictedDate}
                format={DateTimeDisplayFormat.LONG}
                label={t('overviewLabel.predictedArrival')}
                startDateTime={predictedDate}
                timeZone={timeZone}
              />
            </Flex>
            <Flex>{isStale ? t('tooltips.stale') : ''}</Flex>
          </Flex>
        );
    }
  };

  return (
    <DateComponentWrapper
      isStacked={format === DateTimeDisplayFormat.STACKED}
      data-testid="predicted-timestamp"
      css={{
        ...css,
      }}
    >
      <Tooltip title={getTooltip()}>
        <Typography variant="subtext">
          <DateTime
            css={{
              background: `${
                format !== DateTimeDisplayFormat.STACKED ? bgColor : 'inherit'
              }`,
            }}
            dateDiff={
              format === DateTimeDisplayFormat.STACKED && (
                <DateDiff
                  dateToCompare={predictedDate}
                  plannedEndDate={plannedEndDate}
                  plannedStartDate={plannedStartDate}
                />
              )
            }
            endDateTime={predictedDate}
            format={format}
            isPrediction={true}
            isStale={isStale}
            label={t('overviewLabel.predictedArrival')}
            startDateTime={predictedDate}
            startIcon={
              format !== DateTimeDisplayFormat.STACKED && (
                <PredictedIcon isStale={isStale} />
              )
            }
            timeZone={timeZone}
          />
        </Typography>
      </Tooltip>
      {format !== DateTimeDisplayFormat.STACKED && (
        <DateDiff
          dateToCompare={predictedDate}
          plannedEndDate={plannedEndDate}
          plannedStartDate={plannedStartDate}
        />
      )}
    </DateComponentWrapper>
  );
};

export default PredictedTimestamp;
